import React, { useState, useEffect } from 'react';
import { FaSyringe, FaUser, FaStethoscope, FaNotesMedical, FaPills, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';

const AnesthesiaForm = ({ anesthesiaData = {}, handleAnesthesiaChange, handleFormSubmission }) => {
  const [showIntraPostIssues, setShowIntraPostIssues] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Check if intra-post operative events checkbox is already checked
    if (anesthesiaData.intraPostOperativeEvents) {
      setShowIntraPostIssues(true);
    }
  }, []);

  const handleIntraPostIssuesChange = (e) => {
    const isChecked = e.target.checked;
    setShowIntraPostIssues(isChecked);
    handleAnesthesiaChange({
      target: {
        name: 'intraPostOperativeEvents',
        value: isChecked,
      },
    });
  };

  const intraPostOpSection = [
    {
      title: 'Critical Events',
      name: 'intraPostCriticalEvents',
      issues: ['Death', 'Cardiac arrest', 'Unplanned ICU admission (or equivalent)']
    },
    {
      title: 'Airway/Pulmonary Events',
      name: 'intraPostAirwayPulmonaryEvents',
      issues: ['Respiratory arrest', 'Pulmonary edema', 'Bronchospasm required treatment (Rx)', 'Aspiration', 'Laryngospasm required succinylcholine treatment (Rx)', 'Intubation/Reintubation (unplanned)', 'Inability to secure airway', 'Pneumothorax', 'Unanticipated difficult airway', 'Retained throat pack (oral surgery)']
    },
    {
      title: 'Cardiac Events',
      name: 'intraPostCardiacEvents', 
      issues: ['Myocardial ischemia, indicated by clinical symptoms (Sx), ST segment changes or echocardiography', 'New premature ventricular contractions (PVCs), bradycardia, atrial fibrillation, or other dysrhythmias required unanticipated treatment (Rx)', 'Hypotension required unanticipated treatment (Rx) with a continuous pressor infusion', 'Myocardial infarction']
    },
    {
      title: 'Medication/Transfusion Events',
      name: 'intraPostMedicationTransfusionEvents',
      issues: ['Medication error', 'Anaphylaxis', 'Unanticipated medication reaction', 'Unanticipated transfusion', 'Use of sedation/narcotic reversal treatment (Rx)', 'Transfusion reaction', 'Malignant Hyperthermia', 'Local anesthetic systemic toxicity']
    },
    {
      title: 'Neurological/Other Events',
      name: 'intraPostNeurologicalOtherEvents',
      issues: ['Seizure', 'New stroke', 'Peripheral neurologic deficit', 'Uncontrolled blood sugar required treatment (Rx)', 'Position injury', 'Fall from chair/OR table']
    },
    {
      title: 'Equipment/Environment Events',
      name: 'intraPostEquipmentEnvironmentEvents',
      issues: ['Equipment problem', 'Surgical fire', 'Burn injury']
    },
    {
      title: 'Process Events',
      name: 'intraPostProcessEvents',
      issues: ['Unplanned outpatient admission', 'Emergency/Altered workflow', 'Unplanned hospital admission', 'Unplanned return to OR', 'Case canceled (includes pre-OR)', 'Incorrect patient', 'Incorrect site or procedure']
    },
    {
      title: 'Other Events',
      name: 'intraPostOtherEvents',
      issues: ['Incorrect tooth removed']
    }
  ];

  const medications = ['Propofol', 'Midazolam', 'Ketamine', 'Fentanyl/Remifentanil', 'Dexmedetomidine', 'Narcan', 'Flumazenil'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Small delay to show loading state
    setTimeout(() => {
      handleFormSubmission(e);
      setIsSubmitting(false);
    }, 100);
  };

  // Render checkbox group with modern TailwindPlus styling
  const renderCheckboxGroup = (section, data) => (
    <div key={section.name} className="mt-6">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">{section.title}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
        {section.issues.map((issue) => {
          const currentValue = data[section.name];
          const isChecked = Array.isArray(currentValue) ? currentValue.includes(issue) : (currentValue === issue);
          
          return (
            <div key={issue} className="flex gap-3">
              <div className="flex h-6 shrink-0 items-center">
                <div className="group grid size-4 grid-cols-1">
            <input
              type="checkbox"
              name={section.name}
              value={issue}
                    checked={isChecked}
              onChange={(e) => handleAnesthesiaChange({
                target: {
                  name: section.name,
                        value: Array.isArray(data[section.name]) 
                          ? (e.target.checked
                    ? [...(data[section.name] || []), issue]
                            : (data[section.name] || []).filter((item) => item !== issue))
                          : (e.target.checked ? issue : ''),
                },
              })}
                    className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                  />
                  <svg
                    fill="none"
                    viewBox="0 0 14 14"
                    className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25"
                  >
                    <path
                      d="M3 8L6 11L11 3.5"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="opacity-0 group-has-[:checked]:opacity-100"
                    />
                    <path
                      d="M3 7H11"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="opacity-0 group-has-[:indeterminate]:opacity-100"
                    />
                  </svg>
                </div>
              </div>
              <div className="text-sm/6">
                <label className="font-medium text-gray-900 cursor-pointer">
            {issue}
          </label>
      </div>
    </div>
  );
        })}
    </div>
  </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex size-16 items-center justify-center rounded-full bg-blue-100">
            <FaSyringe className="size-8 text-blue-600" aria-hidden="true" />
          </div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Anesthesia Procedure Form
          </h1>
          <p className="mt-2 text-base text-gray-600">
            Complete the anesthesia procedure information
          </p>
        </div>

        {/* Form Card */}
        <div className="overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-gray-900/5">
          <form onSubmit={handleSubmit}>
            <div className="space-y-12 px-6 py-8 sm:px-10 sm:py-10">
              {/* Patient Demographics Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaUser className="size-5 text-gray-400" />
                  Patient Demographics
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Basic patient information for the anesthesia procedure
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                  <div className="sm:col-span-2">
                    <label htmlFor="age" className="block text-sm/6 font-medium text-gray-900">
                      Age (years)
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <div className="mt-2">
            <input
                        id="age"
                        name="age"
              type="number"
                        value={anesthesiaData.age || ''}
              onChange={handleAnesthesiaChange}
              required
                        placeholder="Enter age"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="weight" className="block text-sm/6 font-medium text-gray-900">
                      Weight (lb)
                      <span className="text-red-500 ml-1">*</span>
          </label>
                    <div className="mt-2">
            <input
                        id="weight"
                        name="weight"
              type="number"
                        value={anesthesiaData.weight || ''}
              onChange={handleAnesthesiaChange}
              required
                        placeholder="Enter weight"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="dateOfSurgery" className="block text-sm/6 font-medium text-gray-900">
                      Date of Surgery
                      <span className="text-red-500 ml-1">*</span>
          </label>
                    <div className="mt-2">
            <input
                        id="dateOfSurgery"
                        name="dateOfSurgery"
              type="date"
                        value={anesthesiaData.dateOfSurgery || ''}
              onChange={handleAnesthesiaChange}
              required
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
            />
                    </div>
        </div>

                  <div className="sm:col-span-3">
                    <fieldset>
                      <legend className="block text-sm/6 font-medium text-gray-900 mb-4">
                        Sex
                      </legend>
                      <div className="flex flex-wrap gap-x-6 gap-y-3">
                        {['Male', 'Female'].map((sex) => (
                          <div key={sex} className="flex items-center">
              <input
                              id={`sex-${sex}`}
                name="sex"
                type="radio"
                              value={sex}
                              checked={anesthesiaData.sex === sex}
                onChange={(e) => handleAnesthesiaChange({
                                target: { name: 'sex', value: e.target.value },
                })}
                              className="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden [&:not(:checked)]:before:hidden"
              />
                            <label htmlFor={`sex-${sex}`} className="ml-3 block text-sm/6 font-medium text-gray-900 cursor-pointer">
                              {sex}
            </label>
                          </div>
                        ))}
                      </div>
                    </fieldset>
                  </div>
          </div>
        </div>

              {/* Anesthesia Classification Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaStethoscope className="size-5 text-gray-400" />
                  Anesthesia Classification
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  American Society of Anesthesiologists Classification
                </p>

                <div className="mt-6">
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
            {['I', 'II', 'III', 'IV', 'E'].map((grade) => (
                      <div key={grade} className="flex gap-3">
                        <div className="flex h-6 shrink-0 items-center">
                          <div className="group grid size-4 grid-cols-1">
                <input
                  type="checkbox"
                  name="americanSocietyAnesthesiologistsClassification"
                  value={grade}
                  checked={anesthesiaData.americanSocietyAnesthesiologistsClassification === grade}
                  onChange={(e) => handleAnesthesiaChange({
                    target: { name: 'americanSocietyAnesthesiologistsClassification', value: e.target.checked ? grade : '' },
                  })}
                              className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                            />
                            <svg
                              fill="none"
                              viewBox="0 0 14 14"
                              className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25"
                            >
                              <path
                                d="M3 8L6 11L11 3.5"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="opacity-0 group-has-[:checked]:opacity-100"
                              />
                            </svg>
                          </div>
                        </div>
                        <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">
                {grade}
              </label>
                      </div>
            ))}
          </div>
        </div>
              </div>

              {/* Anesthesia Details Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaSyringe className="size-5 text-gray-400" />
                  Anesthesia Details
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Select anesthesia type and provider information
                </p>

                <div className="mt-10 space-y-8">
                  <fieldset>
                    <legend className="block text-sm/6 font-medium text-gray-900 mb-4">
                      Anesthesia Type
                    </legend>
                    <div className="flex flex-wrap gap-x-6 gap-y-3">
            {['General', 'Local', 'Nitrous Oxide', 'IV Sedation'].map((type) => (
                        <div key={type} className="flex gap-3">
                          <div className="flex h-6 shrink-0 items-center">
                            <div className="group grid size-4 grid-cols-1">
                <input
                  type="checkbox"
                  name="anesthesiaType"
                  value={type}
                  checked={anesthesiaData.anesthesiaType === type}
                  onChange={(e) => handleAnesthesiaChange({
                    target: { name: 'anesthesiaType', value: e.target.checked ? type : '' },
                  })}
                                className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                              />
                              <svg
                                fill="none"
                                viewBox="0 0 14 14"
                                className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25"
                              >
                                <path
                                  d="M3 8L6 11L11 3.5"
                                  strokeWidth={2}
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="opacity-0 group-has-[:checked]:opacity-100"
                                />
                              </svg>
                            </div>
                          </div>
                          <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">
                {type}
              </label>
                        </div>
            ))}
          </div>
                  </fieldset>

                  <fieldset>
                    <legend className="block text-sm/6 font-medium text-gray-900 mb-4">
                      Anesthesia Provider
                    </legend>
                    <div className="flex flex-wrap gap-x-6 gap-y-3">
            {['Surgeon', 'Anesthesiologist', 'Certified Registered Nurse Anesthetist'].map((provider) => (
                        <div key={provider} className="flex gap-3">
                          <div className="flex h-6 shrink-0 items-center">
                            <div className="group grid size-4 grid-cols-1">
                <input
                  type="checkbox"
                  name="anesthesiaProvider"
                  value={provider}
                  checked={anesthesiaData.anesthesiaProvider === provider}
                  onChange={(e) => handleAnesthesiaChange({
                    target: { name: 'anesthesiaProvider', value: e.target.checked ? provider : '' },
                  })}
                                className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                              />
                              <svg
                                fill="none"
                                viewBox="0 0 14 14"
                                className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25"
                              >
                                <path
                                  d="M3 8L6 11L11 3.5"
                                  strokeWidth={2}
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="opacity-0 group-has-[:checked]:opacity-100"
                                />
                              </svg>
                            </div>
                          </div>
                          <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">
                {provider}
              </label>
                        </div>
            ))}
          </div>
                  </fieldset>

                  <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                    <div className="sm:col-span-3">
                      <label htmlFor="surgicalTime" className="block text-sm/6 font-medium text-gray-900">
                        Surgical Time (minutes)
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="mt-2">
            <input
                          id="surgicalTime"
                          name="surgicalTime"
              type="number"
                          value={anesthesiaData.surgicalTime || ''}
              onChange={handleAnesthesiaChange}
              required
                          placeholder="Enter surgical time"
                          className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                        />
                      </div>
                    </div>

                    <div className="sm:col-span-3">
                      <label htmlFor="primaryProcedure" className="block text-sm/6 font-medium text-gray-900">
                        Primary Procedure
          </label>
                      <div className="mt-2">
            <input
                          id="primaryProcedure"
                          name="primaryProcedure"
              type="text"
                          value={anesthesiaData.primaryProcedure || ''}
              onChange={handleAnesthesiaChange}
                          placeholder="Enter primary procedure"
                          className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
            />
                      </div>
                    </div>
                  </div>
                </div>
        </div>

              {/* Co-Morbidities Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaNotesMedical className="size-5 text-gray-400" />
                  Co-Morbidities
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Select all applicable co-morbidities
                </p>

                <div className="mt-6">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {['Upper Respiratory Infection', 'Asthma', 'Smoker', 'Snorer/Obstructive Sleep Apnea', 'Hypertension', 'Cardiovascular Disease', 'Diabetes Mellitus', 'Developmental Delay', 'Malnourished', 'Other'].map(condition => {
                      const isChecked = Array.isArray(anesthesiaData.coMorbidities) 
                        ? anesthesiaData.coMorbidities.includes(condition)
                        : false;
                      
                      return (
                        <div key={condition} className="flex gap-3">
                          <div className="flex h-6 shrink-0 items-center">
                            <div className="group grid size-4 grid-cols-1">
                <input
                  type="checkbox"
                  name="coMorbidities"
                  value={condition}
                                checked={isChecked}
                  onChange={(e) => handleAnesthesiaChange({
                    target: {
                      name: 'coMorbidities',
                      value: e.target.checked 
                                      ? [...(Array.isArray(anesthesiaData.coMorbidities) ? anesthesiaData.coMorbidities : []), condition]
                                      : (Array.isArray(anesthesiaData.coMorbidities) ? anesthesiaData.coMorbidities.filter(item => item !== condition) : [])
                                  }
                                })}
                                className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                              />
                              <svg
                                fill="none"
                                viewBox="0 0 14 14"
                                className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25"
                              >
                                <path
                                  d="M3 8L6 11L11 3.5"
                                  strokeWidth={2}
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="opacity-0 group-has-[:checked]:opacity-100"
                                />
                              </svg>
                            </div>
                          </div>
                          <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">
                {condition}
              </label>
                        </div>
                      );
                    })}
                  </div>
                  {(Array.isArray(anesthesiaData.coMorbidities) && anesthesiaData.coMorbidities.includes('Other')) && (
                    <div className="mt-4">
            <input
              type="text"
              name="coMorbiditiesOther"
              placeholder="Specify other co-morbidities"
              value={anesthesiaData.coMorbiditiesOther || ''}
              onChange={handleAnesthesiaChange}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
            />
                    </div>
                  )}
          </div>
        </div>

              {/* Intra/Post-Operative Events Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaExclamationTriangle className="size-5 text-gray-400" />
                  Intra/Post-Operative Events
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Report any events that occurred during or after the procedure
                </p>

                <div className="mt-6">
                  <div className="flex gap-3">
                    <div className="flex h-6 shrink-0 items-center">
                      <div className="group grid size-4 grid-cols-1">
            <input
              type="checkbox"
              name="intraPostOperativeEvents"
              checked={showIntraPostIssues}
              onChange={handleIntraPostIssuesChange}
                          className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                        />
                        <svg
                          fill="none"
                          viewBox="0 0 14 14"
                          className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25"
                        >
                          <path
                            d="M3 8L6 11L11 3.5"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="opacity-0 group-has-[:checked]:opacity-100"
                          />
                        </svg>
                      </div>
                    </div>
                    <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">
                      Were there any intraoperative or post operative events?
                    </label>
                  </div>
                </div>

                {showIntraPostIssues && (
                  <div className="mt-8 space-y-8">
                    {intraPostOpSection.map((section) => renderCheckboxGroup(section, anesthesiaData))}
                  </div>
                )}
              </div>

              {/* Medication and Dose Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaPills className="size-5 text-gray-400" />
                  Medication and Dose Administered
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Select medications used and specify doses
                </p>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                  {medications.map((medication) => {
                    const isChecked = Array.isArray(anesthesiaData.medications) 
                      ? anesthesiaData.medications.includes(medication)
                      : false;
                    
                    return (
                      <div key={medication} className="flex items-center gap-4">
                        <div className="flex h-6 shrink-0 items-center">
                          <div className="group grid size-4 grid-cols-1">
                            <input
                              type="checkbox"
                              name="medications"
                              value={medication}
                              checked={isChecked}
                              onChange={(e) =>
                                handleAnesthesiaChange({
                                  target: {
                                    name: 'medications',
                                    value: e.target.checked
                                      ? [...(Array.isArray(anesthesiaData.medications) ? anesthesiaData.medications : []), medication]
                                      : (Array.isArray(anesthesiaData.medications) ? anesthesiaData.medications.filter((item) => item !== medication) : []),
                                  },
                                })
                              }
                              className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                            />
                            <svg
                              fill="none"
                              viewBox="0 0 14 14"
                              className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25"
                            >
                              <path
                                d="M3 8L6 11L11 3.5"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="opacity-0 group-has-[:checked]:opacity-100"
                              />
                            </svg>
                          </div>
                        </div>
                        <label className="text-sm/6 font-medium text-gray-900 cursor-pointer min-w-[150px]">
                          {medication}
                        </label>
                        {isChecked && (
                          <input
                            type="text"
                            placeholder="Dose"
                            name={`${medication}Dose`}
                            value={anesthesiaData[`${medication}Dose`] || ''}
                            onChange={handleAnesthesiaChange}
                            className="flex-1 rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                          />
                        )}
                      </div>
                    );
                  })}
                  
                  {/* Other Medication */}
                  <div className="flex items-center gap-4">
                    <div className="flex h-6 shrink-0 items-center">
                      <div className="group grid size-4 grid-cols-1">
                        <input
                          type="checkbox"
                          name="medications"
                          value="Other"
                          checked={Array.isArray(anesthesiaData.medications) && anesthesiaData.medications.includes("Other")}
                          onChange={(e) =>
                            handleAnesthesiaChange({
                              target: {
                                name: 'medications',
                                value: e.target.checked
                                  ? [...(Array.isArray(anesthesiaData.medications) ? anesthesiaData.medications : []), "Other"]
                                  : (Array.isArray(anesthesiaData.medications) ? anesthesiaData.medications.filter((item) => item !== "Other") : []),
                              },
                            })
                          }
                          className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                        />
                        <svg
                          fill="none"
                          viewBox="0 0 14 14"
                          className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25"
                        >
                          <path
                            d="M3 8L6 11L11 3.5"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="opacity-0 group-has-[:checked]:opacity-100"
                          />
                        </svg>
                      </div>
                    </div>
                    <label className="text-sm/6 font-medium text-gray-900 cursor-pointer min-w-[150px]">
                      Other
          </label>
                    {Array.isArray(anesthesiaData.medications) && anesthesiaData.medications.includes("Other") && (
                      <>
                        <input
                          type="text"
                          placeholder="Enter Medication"
                          name="otherMedication"
                          value={anesthesiaData.otherMedication || ''}
                          onChange={handleAnesthesiaChange}
                          className="flex-1 rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                        />
                        <input
                          type="text"
                          placeholder="Dose"
                          name="otherDose"
                          value={anesthesiaData.otherDose || ''}
                          onChange={handleAnesthesiaChange}
                          className="flex-1 rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-center gap-x-4 border-t border-gray-200 px-6 py-4 sm:px-10">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center gap-x-2 rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-0.5 size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <FaCheckCircle aria-hidden="true" className="-ml-0.5 size-5" />
                    <span>Submit Form</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AnesthesiaForm;
